import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { NoticeService } from 'src/app/services/notice/notice.service';

export interface EventItem {
  day: number;
  month: string;
  title: string;
  time: string;
  location: string;
  description: string;
  pdfUrl?: string;
  imageUrl?: string;
}

@Component({
  selector: 'app-recent-events',
  templateUrl: './recent-events.component.html',
  styleUrls: ['./recent-events.component.scss']
})
export class RecentEventsComponent implements OnInit, OnDestroy {
  activeTab: 'happening' | 'upcoming' | 'expired' = 'happening';

  happeningEvents: EventItem[] = [
    {
      day: 20,
      month: 'Apr',
      title: 'Farewell Party 2026',
      time: '',
      location: 'SSGC, Sirsa',
      description: 'A vibrant and emotional send-off for our graduating seniors, celebrating their journey and achievements at SSGC.',
      imageUrl: 'assets/img/events/farewell.jpg'
    }
  ];

  upcomingEvents: EventItem[] = [];

  expiredEvents: EventItem[] = [
    {
      day: 20,
      month: 'Mar',
      title: 'Splash of Sustainability: Holi Seminar',
      time: '',
      location: 'SSGC, Sirsa',
      description: 'A seminar focused on celebrating an eco-friendly Holi and promoting environmental sustainability among students.',
      imageUrl: 'assets/img/events/holi.jpg'
    },
    {
      day: 15,
      month: 'Mar',
      title: 'Road Safety Awareness Activity',
      time: '',
      location: 'SSGC, Sirsa',
      description: 'Educating students on traffic rules and road safety measures to ensure responsible citizenship and personal safety.',
      imageUrl: 'assets/img/events/road-safety.jpg'
    }
  ];

  cmsHappeningEvents: EventItem[] = [];
  cmsUpcomingEvents: EventItem[] = [];
  cmsExpiredEvents: EventItem[] = [];
  selectedEvent: EventItem | null = null;
  private sub: Subscription;

  get currentEvents(): EventItem[] {
    switch (this.activeTab) {
      case 'happening': 
        return [...this.cmsHappeningEvents, ...this.happeningEvents];
      case 'upcoming': 
        return [...this.cmsUpcomingEvents, ...this.upcomingEvents];
      case 'expired': 
        return [...this.cmsExpiredEvents, ...this.expiredEvents];
    }
  }

  get totalHappeningCount(): number {
    return this.happeningEvents.length + this.cmsHappeningEvents.length;
  }

  get totalUpcomingCount(): number {
    return this.upcomingEvents.length + this.cmsUpcomingEvents.length;
  }

  get totalExpiredCount(): number {
    return this.expiredEvents.length + this.cmsExpiredEvents.length;
  }

  setTab(tab: 'happening' | 'upcoming' | 'expired'): void {
    this.activeTab = tab;
  }

  openEventModal(event: EventItem): void {
    this.selectedEvent = event;
  }

  closeEventModal(): void {
    this.selectedEvent = null;
  }

  constructor(private noticeService: NoticeService) { }

  ngOnInit(): void {
    const now = new Date();
    const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const todayEnd = todayStart + 24 * 60 * 60 * 1000 - 1;

    this.sub = this.noticeService.getEventsList().snapshotChanges().subscribe((list) => {
      const happening: EventItem[] = [];
      const upcoming: EventItem[] = [];
      const expired: EventItem[] = [];

      list.forEach((item) => {
        const val = item.payload.val() || {};
        const parsedDate = this.parseEventDate(val.date || '', val.monthYear || '');
        const attachments = this.resolveAttachments(val);
        
        let eventTime = (val.time || '').trim();
        if (eventTime === '12:00 Am - 12:00 Am' || eventTime === '12:00 AM - 12:00 AM') {
          eventTime = '';
        }

        const eventItem: EventItem = {
          day: parsedDate.day,
          month: parsedDate.month,
          title: val.title || val.name || 'Untitled Event',
          time: eventTime,
          location: val.location || 'SSGC, Sirsa',
          description: val.description || val.details || '',
          pdfUrl: attachments.pdfUrl,
          imageUrl: attachments.imageUrl
        };

        const t = parsedDate.timeMs;
        if (t === 0) {
          happening.push(eventItem);
        } else if (t < todayStart) {
          expired.push(eventItem);
        } else if (t > todayEnd) {
          upcoming.push(eventItem);
        } else {
          happening.push(eventItem);
        }
      });

      this.cmsHappeningEvents = happening.reverse();
      this.cmsUpcomingEvents = upcoming.reverse();
      this.cmsExpiredEvents = expired.reverse();
    }, (error) => {
      console.error('Error fetching events:', error);
    });
  }

  isAttachmentURL(value: string, keyName: string): boolean {
    if (!value || typeof value !== 'string') return false;
    const valLower = value.toLowerCase().trim();

    return (
      valLower.startsWith('http://') ||
      valLower.startsWith('https://') ||
      valLower.startsWith('assets/') ||
      valLower.includes('firebasestorage') ||
      valLower.includes('/notices/') ||
      valLower.includes('/uploads/') ||
      valLower.includes('.pdf') ||
      valLower.includes('.png') ||
      valLower.includes('.jpg') ||
      valLower.includes('.jpeg') ||
      valLower.includes('.webp') ||
      valLower.includes('.gif') ||
      valLower.includes('.svg')
    );
  }

  resolveAttachments(val: any): { pdfUrl: string, imageUrl: string } {
    let pdfUrl = '';
    let imageUrl = '';

    if (val.pdfUrl && typeof val.pdfUrl === 'string') pdfUrl = val.pdfUrl.trim();
    else if (val.pdfURL && typeof val.pdfURL === 'string') pdfUrl = val.pdfURL.trim();
    else if (val.pdf && typeof val.pdf === 'string') pdfUrl = val.pdf.trim();

    if (val.imageUrl && typeof val.imageUrl === 'string') imageUrl = val.imageUrl.trim();
    else if (val.image && typeof val.image === 'string') imageUrl = val.image.trim();
    else if (val.img && typeof val.img === 'string') imageUrl = val.img.trim();
    else if (val.imageURL && typeof val.imageURL === 'string') imageUrl = val.imageURL.trim();
    else if (val.photo && typeof val.photo === 'string') imageUrl = val.photo.trim();
    else if (val.pic && typeof val.pic === 'string') imageUrl = val.pic.trim();

    for (const key in val) {
      if (val.hasOwnProperty(key) && typeof val[key] === 'string') {
        const valueStr = val[key].trim();
        if (this.isAttachmentURL(valueStr, key)) {
          const lowerKey = key.toLowerCase();
          const lowerValue = valueStr.toLowerCase();

          const hasPdfExt = lowerValue.includes('.pdf') || lowerValue.includes('.pdf?');
          const hasImgExt = lowerValue.includes('.png') || 
                            lowerValue.includes('.jpg') || 
                            lowerValue.includes('.jpeg') || 
                            lowerValue.includes('.webp') || 
                            lowerValue.includes('.gif') || 
                            lowerValue.includes('.svg');

          if (hasPdfExt) {
            if (!pdfUrl) pdfUrl = valueStr;
          } else if (hasImgExt) {
            if (!imageUrl) imageUrl = valueStr;
          } else {
            if (lowerKey.includes('pdf')) {
              if (!pdfUrl) pdfUrl = valueStr;
            } else if (
              lowerKey.includes('image') ||
              lowerKey.includes('img') ||
              lowerKey.includes('photo') ||
              lowerKey.includes('pic')
            ) {
              if (!imageUrl) imageUrl = valueStr;
            } else {
              if (!pdfUrl && !imageUrl) {
                pdfUrl = valueStr;
              }
            }
          }
        }
      }
    }

    return { pdfUrl, imageUrl };
  }

  parseEventDate(dateStr: string, monthYearStr?: string): { day: number, month: string, timeMs: number } {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    
    if (!dateStr) {
      return { day: 1, month: 'Current', timeMs: 0 };
    }

    const trimmedDate = dateStr.trim();
    const trimmedMonthYear = monthYearStr ? monthYearStr.trim() : '';

    let dateToParse = trimmedDate;
    if (trimmedDate.length <= 2 && trimmedMonthYear) {
      dateToParse = `${trimmedDate} ${trimmedMonthYear}`;
    }

    // 1. Check Unix timestamp
    const num = Number(trimmedDate);
    if (!isNaN(num) && num > 1000000000) {
      const ms = num < 9999999999 ? num * 1000 : num;
      const d = new Date(ms);
      return {
        day: d.getDate(),
        month: months[d.getMonth()] || 'Current',
        timeMs: ms
      };
    }

    // 2. Check numeric dd-MM-yyyy
    const numericMatch = dateToParse.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})$/);
    if (numericMatch) {
      const day = Number(numericMatch[1]);
      const monthIdx = Number(numericMatch[2]) - 1;
      const year = Number(numericMatch[3]);
      const dateObj = new Date(year, monthIdx, day);
      return {
        day: day,
        month: months[monthIdx] || 'Current',
        timeMs: dateObj.getTime()
      };
    }

    // 3. Check alpha month dd-MMM-yyyy (e.g. 20-Jul-2026)
    const alphaMatch = dateToParse.match(/^(\d{1,2})[-/\s]+([A-Za-z]+)[-/\s]+(\d{4})$/i);
    if (alphaMatch) {
      const day = Number(alphaMatch[1]);
      const monthStr = alphaMatch[2].substring(0, 3);
      const year = Number(alphaMatch[3]);
      const tmp = new Date(`${monthStr} 1, ${year}`);
      const monthIdx = isNaN(tmp.getTime()) ? 0 : tmp.getMonth();
      const dateObj = new Date(year, monthIdx, day);
      return {
        day: day,
        month: months[monthIdx] || monthStr,
        timeMs: dateObj.getTime()
      };
    }

    // 4. Direct Date.parse
    const parsed = Date.parse(dateToParse);
    if (!isNaN(parsed)) {
      const d = new Date(parsed);
      return {
        day: d.getDate(),
        month: months[d.getMonth()] || 'Current',
        timeMs: parsed
      };
    }

    // 5. Fallback starts with number
    const startNumMatch = dateToParse.match(/^(\d{1,2})/);
    if (startNumMatch) {
      const dayNum = parseInt(startNumMatch[1], 10);
      let displayMonth = 'Current';
      if (trimmedMonthYear) {
        displayMonth = trimmedMonthYear.split(/\s+/)[0].substring(0, 3);
      } else {
        const wordMatch = dateToParse.match(/[A-Za-z]+/);
        if (wordMatch) {
          displayMonth = wordMatch[0].substring(0, 3);
        }
      }
      return { day: dayNum, month: displayMonth, timeMs: 0 };
    }

    return { day: 1, month: 'Current', timeMs: 0 };
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
